'use client';

import { useEffect, useId, useRef, useState } from 'react';

import styles from './website-hero-landscape.module.css';

export function WebsiteHeroLandscape() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const clipId = useId();
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    let active = true;
    const images = [
      '/images/editorial/hero-clouds-exact-plate.png',
      '/images/editorial/product-horizon.png',
    ].map((src) => {
      const image = new Image();
      image.src = src;
      return image.decode();
    });
    void Promise.allSettled(images).then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = true;
    const sync = () => {
      if (motion.matches || document.hidden || !visible) video.pause();
      else {
        if (!video.getAttribute('src'))
          video.src = '/images/editorial/hero-clouds-exact.mp4';
        video.muted = true;
        video.playbackRate = 1;
        void video.play().catch(() => {});
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    observer.observe(video.closest('section') ?? video);
    video.addEventListener('canplay', sync);
    window.addEventListener('pointerdown', sync);
    sync();
    motion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      video.pause();
      observer.disconnect();
      video.removeEventListener('canplay', sync);
      window.removeEventListener('pointerdown', sync);
      motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);
  useEffect(() => {
    const section = videoRef.current?.closest('section');
    if (!section) return;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const progress = motion.matches
        ? 0
        : Math.min(1, Math.max(0, -rect.top / rect.height));
      section.style.setProperty('--landscape-back-y', `${progress * 96}px`);
      section.style.setProperty('--landscape-front-y', `${progress * -72}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    motion.addEventListener('change', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      motion.removeEventListener('change', schedule);
      section.style.removeProperty('--landscape-back-y');
      section.style.removeProperty('--landscape-front-y');
    };
  }, []);
  return (
    <>
      <div className={styles.plate} aria-hidden="true" />
      <video
        ref={videoRef}
        data-hero-clouds
        data-playing={playing}
        onPlaying={() => setPlaying(true)}
        className={styles.clouds}
        poster="/images/editorial/hero-clouds-exact-plate.png"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className={styles.shade} aria-hidden="true" />
      <svg
        className={styles.portal}
        data-ready={ready}
        viewBox="0 0 1672 941"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <clipPath id={`${clipId}-portal`}>
            <path
              clipRule="evenodd"
              fillRule="evenodd"
              d="M1335 548 L1335 134 L1499 0 L1672 0 L1672 597 L1611 583 L1611 145 L1476 198 L1476 550 Z"
            />
          </clipPath>
        </defs>
        <image
          href="/images/editorial/product-horizon.png"
          width="1672"
          height="941"
          clipPath={`url(#${clipId}-portal)`}
        />
      </svg>
      <svg
        className={styles.foreground}
        data-ready={ready}
        data-hero-foreground
        viewBox="0 0 1672 941"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <clipPath id={clipId}>
            {/* The original cliff is cut out with native vector alpha, never a painted checkerboard. */}
            <path d="M0 873 L44 854 L83 845 L120 831 L168 830 L214 820 L262 811 L304 801 L331 790 L370 784 L405 775 L449 767 L499 758 L548 748 L578 744 L618 734 L630 717 L663 709 L703 700 L723 691 L751 682 L765 659 L793 649 L804 635 L841 623 L890 616 L957 612 L1025 600 L1068 596 L1115 586 L1162 575 L1208 568 L1263 552 L1306 548 L1340 546 L1460 552 L1611 572 L1672 596 L1672 941 L0 941 Z" />
          </clipPath>
        </defs>
        <image
          href="/images/editorial/product-horizon.png"
          width="1672"
          height="941"
          clipPath={`url(#${clipId})`}
        />
      </svg>
    </>
  );
}
